/**
 * Organization Store - Vue 2 Reactive State Management
 * Manages current organization context, members, and permissions
 */
import Vue from 'vue';
import organizationService from '@/services/organizationService';
// ============================================================================
// Organization Store Class
// ============================================================================
class OrganizationStore {
    state;
    constructor() {
        this.state = Vue.observable({
            organizations: [],
            currentOrganization: null,
            members: [],
            invitations: [],
            permissions: [],
            loading: false,
            error: null,
            lastOrgId: this.getLastOrgIdFromStorage()
        });
    }
    // ==========================================================================
    // Getters
    // ==========================================================================
    get organizations() {
        return this.state.organizations;
    }
    get currentOrganization() {
        return this.state.currentOrganization;
    }
    get currentOrgId() {
        return this.state.currentOrganization?.id || null;
    }
    get members() {
        return this.state.members;
    }
    get invitations() {
        return this.state.invitations;
    }
    get permissions() {
        return this.state.permissions;
    }
    get loading() {
        return this.state.loading;
    }
    get error() {
        return this.state.error;
    }
    get personalOrganization() {
        return this.state.organizations.find(org => org.is_personal === true) || null;
    }
    get teamOrganizations() {
        return this.state.organizations.filter(org => org.is_personal === false);
    }
    get currentUserRole() {
        if (!this.state.currentOrganization)
            return null;
        const member = this.state.members.find(m => m.organization_id === this.state.currentOrganization.id);
        return member?.role || null;
    }
    get isOwner() {
        return this.currentUserRole === 'owner';
    }
    get isAdmin() {
        return this.currentUserRole === 'admin' || this.currentUserRole === 'owner';
    }
    // ==========================================================================
    // LocalStorage Helpers
    // ==========================================================================
    getLastOrgIdFromStorage() {
        try {
            return localStorage.getItem('sparkconverse:lastOrgId');
        }
        catch {
            return null;
        }
    }
    saveLastOrgIdToStorage(orgId) {
        try {
            localStorage.setItem('sparkconverse:lastOrgId', orgId);
        }
        catch (error) {
            console.warn('[organizationStore] Failed to save lastOrgId to localStorage:', error);
        }
    }
    // ==========================================================================
    // Actions - Organizations
    // ==========================================================================
    async loadOrganizations() {
        this.state.loading = true;
        this.state.error = null;
        try {
            const orgs = await organizationService.listOrganizations();
            this.state.organizations = orgs;
            // Auto-select organization
            if (orgs.length > 0) {
                // Priority: last selected > personal > first team
                const lastOrg = this.state.lastOrgId ? orgs.find(o => o.id === this.state.lastOrgId) : null;
                const personalOrg = orgs.find(o => o.is_personal === true);
                const selectedOrg = lastOrg || personalOrg || orgs[0];
                await this.selectOrganization(selectedOrg.id);
            }
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to load organizations:', error);
        }
        finally {
            this.state.loading = false;
        }
    }
    async selectOrganization(orgId) {
        const org = this.state.organizations.find(o => o.id === orgId);
        if (!org) {
            this.state.error = `Organization not found: ${orgId}`;
            return;
        }
        this.state.currentOrganization = org;
        this.state.lastOrgId = orgId;
        this.saveLastOrgIdToStorage(orgId);
        // Load organization-specific data
        await Promise.all([
            this.loadMembers(orgId),
            this.loadInvitations(orgId)
            // TODO: Permissions API uses different URL structure (/api/permissions/:resourceType/:resourceId)
            // this.loadPermissions(orgId)
        ]);
    }
    async createOrganization(params) {
        this.state.loading = true;
        this.state.error = null;
        try {
            const org = await organizationService.createOrganization(params);
            this.state.organizations.push(org);
            await this.selectOrganization(org.id);
            return org;
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to create organization:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async updateOrganization(orgId, params) {
        this.state.loading = true;
        this.state.error = null;
        try {
            const updated = await organizationService.updateOrganization(orgId, params);
            // Update in list
            const index = this.state.organizations.findIndex(o => o.id === orgId);
            if (index !== -1) {
                Vue.set(this.state.organizations, index, updated);
            }
            // Update current if selected
            if (this.state.currentOrganization?.id === orgId) {
                this.state.currentOrganization = updated;
            }
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to update organization:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async deleteOrganization(orgId) {
        this.state.loading = true;
        this.state.error = null;
        try {
            await organizationService.deleteOrganization(orgId);
            // Remove from list
            this.state.organizations = this.state.organizations.filter(o => o.id !== orgId);
            // Switch to personal org if current org was deleted
            if (this.state.currentOrganization?.id === orgId) {
                const personalOrg = this.personalOrganization;
                if (personalOrg) {
                    await this.selectOrganization(personalOrg.id);
                }
            }
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to delete organization:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    // ==========================================================================
    // Actions - Members
    // ==========================================================================
    async loadMembers(orgId) {
        try {
            this.state.members = await organizationService.listMembers(orgId);
        }
        catch (error) {
            console.error('[organizationStore] Failed to load members:', error);
        }
    }
    async inviteMember(params) {
        if (!this.currentOrgId)
            throw new Error('No organization selected');
        this.state.loading = true;
        this.state.error = null;
        try {
            const token = await organizationService.inviteMember(this.currentOrgId, params);
            this.state.invitations.push(token);
            return token;
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to invite member:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async removeMember(userId) {
        if (!this.currentOrgId)
            throw new Error('No organization selected');
        this.state.loading = true;
        this.state.error = null;
        try {
            await organizationService.removeMember(this.currentOrgId, userId);
            this.state.members = this.state.members.filter(m => m.user_id !== userId);
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to remove member:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async updateMemberRole(userId, role) {
        if (!this.currentOrgId)
            throw new Error('No organization selected');
        this.state.loading = true;
        this.state.error = null;
        try {
            const updated = await organizationService.updateMemberRole(this.currentOrgId, userId, role);
            const index = this.state.members.findIndex(m => m.user_id === userId);
            if (index !== -1) {
                Vue.set(this.state.members, index, updated);
            }
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to update member role:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    // ==========================================================================
    // Actions - Invitations
    // ==========================================================================
    async loadInvitations(orgId) {
        try {
            this.state.invitations = await organizationService.listInvitations(orgId);
        }
        catch (error) {
            console.error('[organizationStore] Failed to load invitations:', error);
        }
    }
    async acceptInvitation(token) {
        this.state.loading = true;
        this.state.error = null;
        try {
            const member = await organizationService.acceptInvitation(token);
            return member;
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to accept invitation:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async revokeInvitation(tokenId) {
        if (!this.currentOrgId)
            throw new Error('No organization selected');
        this.state.loading = true;
        this.state.error = null;
        try {
            await organizationService.revokeInvitation(this.currentOrgId, tokenId);
            this.state.invitations = this.state.invitations.filter(i => i.id !== tokenId);
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to revoke invitation:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    // ==========================================================================
    // Actions - Permissions (Resource-level)
    // ==========================================================================
    // NOTE: Permissions are resource-scoped (project/category/session), not org-scoped
    // Backend uses /api/permissions/:resourceType/:resourceId
    async loadResourcePermissions(resourceType, resourceId) {
        try {
            this.state.permissions = await organizationService.getResourcePermissions(resourceType, resourceId);
        }
        catch (error) {
            console.error('[organizationStore] Failed to load resource permissions:', error);
        }
    }
    async grantPermission(params) {
        this.state.loading = true;
        this.state.error = null;
        try {
            const permission = await organizationService.grantPermission(params);
            this.state.permissions.push(permission);
            return permission;
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to grant permission:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    async revokePermission(params) {
        this.state.loading = true;
        this.state.error = null;
        try {
            await organizationService.revokePermission(params);
            // Remove from local state
            this.state.permissions = this.state.permissions.filter(p => !(p.resource_type === params.resourceType &&
                p.resource_id === params.resourceId &&
                p.user_id === params.targetUserId));
        }
        catch (error) {
            this.state.error = error.message;
            console.error('[organizationStore] Failed to revoke permission:', error);
            throw error;
        }
        finally {
            this.state.loading = false;
        }
    }
    // ==========================================================================
    // Cleanup
    // ==========================================================================
    clearAll() {
        this.state.organizations = [];
        this.state.currentOrganization = null;
        this.state.members = [];
        this.state.invitations = [];
        this.state.permissions = [];
        this.state.loading = false;
        this.state.error = null;
    }
    clearError() {
        this.state.error = null;
    }
}
// Export singleton instance
export const organizationStore = new OrganizationStore();
export default organizationStore;
